import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useLangPath } from '../../hooks/useLangPath';

// ─── Real product data ─────────────────────────────────────────────────────────
const PLATFORMS = [
  {
    id: 'dineos',
    tag: 'Restaurant Tech · AI',
    name: 'DineOS',
    tagline: 'Restaurant Intelligence Platform',
    color: '#45d0bd',
    icon: '🍽️',
    logo: '/assets/images/dineos-logo.png',
    stat: '30% cost savings',
    description: 'AI-powered operating system for modern restaurants — smart kiosks, KDS, demand forecasting, and multi-branch analytics.',
    url: 'https://pos.wiz-tech.co',
    image: '/assets/images/projects/DineOS/Screenshot 2024-04-25 at 2.03.29.png',
  },
  {
    id: 'qlickhealth',
    tag: 'HealthTech · SaaS',
    name: 'QLick Health',
    tagline: 'Healthcare Management System',
    color: '#8258c8',
    icon: '🏥',
    logo: null,
    stat: 'Zero appointment errors',
    description: 'Medical-grade platform for clinics — collision-proof scheduling, patient records, doctor portals, and a mobile patient app.',
    url: 'https://qlickhealth.ai',
    image: '/assets/images/projects/Medical-Platform/Screenshot 2024-04-25 at 2.11.38.png',
  },
  {
    id: 'safqa',
    tag: 'FinTech · Mobile',
    name: 'Safqa',
    tagline: 'Smart Invoice & Business Finance',
    color: '#2c84c8',
    icon: '💳',
    logo: '/assets/images/safqa-logo.svg',
    stat: '500+ active users',
    description: 'Mobile-first invoicing and financial management for freelancers and SMEs — create, send, and track invoices in under 30 seconds.',
    url: 'https://safqa.wiz-tech.co',
    image: '/assets/images/products/ondemand/Android Compact - 1.png',
  },
];

const PLATFORMS_AR = [
  {
    id: 'dineos',
    tag: 'تقنية المطاعم · ذكاء اصطناعي',
    name: 'DineOS',
    tagline: 'منصة ذكاء المطاعم',
    color: '#45d0bd',
    icon: '🍽️',
    stat: 'توفير 30% من التكاليف',
    description: 'نظام تشغيل مدعوم بالذكاء الاصطناعي للمطاعم الحديثة — أكشاك ذكية وعرض مطبخ وتوقع الطلب وتحليلات متعددة الفروع.',
    url: 'https://pos.wiz-tech.co',
    image: '/assets/images/projects/DineOS/Screenshot 2024-04-25 at 2.03.29.png',
  },
  {
    id: 'qlickhealth',
    tag: 'تقنية الصحة · SaaS',
    name: 'QLick Health',
    tagline: 'نظام إدارة الرعاية الصحية',
    color: '#8258c8',
    icon: '🏥',
    stat: 'صفر أخطاء مواعيد',
    description: 'منصة طبية للعيادات — جدولة مواعيد بلا تعارضات وسجلات مرضى وبوابات أطباء وتطبيق جوال للمريض.',
    url: 'https://qlickhealth.ai',
    image: '/assets/images/projects/Medical-Platform/Screenshot 2024-04-25 at 2.11.38.png',
  },
  {
    id: 'safqa',
    tag: 'تقنية مالية · جوال',
    name: 'Safqa',
    tagline: 'الفواتير الذكية وإدارة الأعمال',
    color: '#2c84c8',
    icon: '💳',
    stat: '+500 مستخدم نشط',
    description: 'فوترة وإدارة مالية للجوال أولاً للمستقلين والشركات الصغيرة — أنشئ وأرسل وتتبع الفواتير في أقل من 30 ثانية.',
    url: 'https://safqa.wiz-tech.co',
    image: '/assets/images/products/ondemand/Android Compact - 1.png',
  },
];

const Service = () => {
  const { t, i18n } = useTranslation();
  const getPath = useLangPath();
  const isRtl = i18n.language === 'ar';
  const platforms = isRtl ? PLATFORMS_AR : PLATFORMS;

  return (
    <section className="service-sec15 v2 ibt-section-gap" style={{ background: 'transparent' }}>

      {/* ── Section header ── */}
      <div className="title-area">
        <div className="container">
          <div className="row end" style={{ alignItems: 'flex-end' }}>
            <div className="col-lg-7">
              <div className="sec-title mb-0">
                <h2 className="title animated-heading">
                  {t('Our Platforms')}
                </h2>
              </div>
            </div>
            <div className="col-lg-5">
              <div className="sec-btn-box">
                <Link to={getPath('/products')} className="ibt-btn ibt-btn-outline">
                  <span>{t('View All Platforms')}</span>
                  <i className="icon-arrow-top" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Swiper ── */}
      <div className="container2">
        <Swiper
          modules={[Navigation, Autoplay]}
          spaceBetween={28}
          loop={true}
          autoplay={{ delay: 4500, disableOnInteraction: false }}
          speed={600}
          breakpoints={{
            320:  { slidesPerView: 1 },
            768:  { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
          }}
          className="ser-slider15"
        >
          {platforms.map((p) => (
            <SwiperSlide key={p.id}>
              <div className="ser-card15" style={{ borderTop: `3px solid ${p.color}` }}>

                {/* Screenshot */}
                <div className="ser-img15" style={{ position: 'relative', overflow: 'hidden' }}>
                  <a href={p.url} target="_blank" rel="noopener noreferrer">
                    <img
                      src={p.image}
                      alt={p.name}
                      style={{ width: '100%', height: '220px', objectFit: 'cover', objectPosition: 'top', display: 'block' }}
                      loading="lazy"
                    />
                    {/* Tag pill over image */}
                    <span style={{
                      position: 'absolute', top: 14, left: 14,
                      fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                      background: `${p.color}cc`, color: '#fff',
                      letterSpacing: isRtl ? 'normal' : '0.5px',
                    }}>
                      {p.tag}
                    </span>
                  </a>
                </div>

                {/* Card body */}
                <div className="ser-content15" style={{ padding: '22px 22px 16px' }}>

                  {/* Name + logo/icon row */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    {p.logo
                      ? <img src={p.logo} alt={p.name} style={{ height: 28, width: 'auto', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
                      : <h4 className="title" style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: '#fff' }}>{p.name}</h4>
                    }
                    <div style={{
                      width: 36, height: 36, borderRadius: 10, fontSize: '1.1rem',
                      background: `${p.color}15`, border: `1px solid ${p.color}30`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {p.icon}
                    </div>
                  </div>

                  {/* Tagline */}
                  <div style={{ color: p.color, fontSize: '11px', fontWeight: 700, marginBottom: 10 }}>
                    {p.tagline}
                  </div>

                  {/* One-liner description */}
                  <p style={{
                    color: '#7878a0', fontSize: '0.82rem', lineHeight: 1.6, margin: '0 0 16px',
                    display: '-webkit-box', WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical', overflow: 'hidden',
                  }}>
                    {p.description}
                  </p>

                  {/* Stat badge */}
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: `${p.color}10`, border: `1px solid ${p.color}25`,
                    borderRadius: 20, padding: '4px 12px',
                    fontSize: '11px', fontWeight: 700, color: p.color,
                  }}>
                    ✦ {p.stat}
                  </div>
                </div>

                {/* Arrow link */}
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ser-btn"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                    padding: '12px 22px 20px', textDecoration: 'none',
                    borderTop: `1px solid ${p.color}18`,
                    background: 'transparent',
                  }}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    border: `1px solid ${p.color}40`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: p.color,
                    transition: 'transform 0.25s ease',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                    </svg>
                  </div>
                </a>

              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

    </section>
  );
};

export default Service;
